<template>
  <b-modal :id="modalId" size="lg" scrollable modal-class="db-modal" @show="alAbrir">
    <template #modal-header>
      <div class="db-modal-head">
        <h2 class="db-modal-title">{{ titulo }}</h2>
        <p class="db-modal-subtitle">
          {{ soloLectura ? "Rol del sistema: sus permisos no se pueden cambiar." : "Elige qué secciones del panel puede gestionar." }}
        </p>
      </div>
      <button type="button" class="db-icon-btn" aria-label="Cerrar" @click="cerrar">
        <span class="material-symbols-outlined">close</span>
      </button>
    </template>

    <form class="db-form" novalidate @submit.prevent="guardar">
      <div class="db-form-row">
        <div class="db-field">
          <label class="db-label" for="mr-nombre">Nombre del rol</label>
          <input
            id="mr-nombre"
            v-model.trim="etiqueta"
            type="text"
            class="db-input"
            maxlength="60"
            :disabled="soloLectura"
            placeholder="Ej.: Catequesis"
          />
        </div>
        <div class="db-field">
          <label class="db-label" for="mr-desc">Descripción</label>
          <input id="mr-desc" v-model.trim="descripcion" type="text" class="db-input" maxlength="500" :disabled="soloLectura" />
        </div>
      </div>

      <fieldset v-for="grupo in grupos" :key="grupo.nombre" class="mr-grupo" :disabled="soloLectura">
        <legend class="mr-grupo-title">{{ grupo.nombre }}</legend>
        <label v-for="p in grupo.permisos" :key="p.name" class="mr-permiso" :class="{ 'is-on': seleccion.includes(p.name) }">
          <input v-model="seleccion" type="checkbox" :value="p.name" :disabled="soloLectura || !puedeConceder(p.name)" />
          <span>
            <span class="mr-permiso-nombre">{{ p.nombre }}</span>
            <span class="db-hint">{{ p.descripcion }}</span>
          </span>
        </label>
      </fieldset>
    </form>

    <template #modal-footer>
      <button type="button" class="db-btn db-btn--ghost" @click="cerrar">{{ soloLectura ? "Cerrar" : "Cancelar" }}</button>
      <button v-if="!soloLectura" type="button" class="db-btn" :disabled="guardando" @click="guardar">
        <span v-if="guardando" class="db-spinner"></span>
        {{ rol ? "Guardar cambios" : "Crear rol" }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import axios from "@/directives/axios.js";
import { avisar, avisarError } from "@/directives/dbDialogs.js";

export default {
  name: "ModalRol",

  props: {
    modalId: { type: String, required: true },
    rol: { type: Object, default: null },
    permisos: { type: Array, default: () => [] },
  },

  data() {
    return {
      etiqueta: "",
      descripcion: "",
      seleccion: [],
      guardando: false,
    };
  },

  computed: {
    soloLectura() {
      return Boolean(this.rol && !this.rol.acciones.editar);
    },

    titulo() {
      if (!this.rol) return "Nuevo rol";
      return this.soloLectura ? this.rol.etiqueta : `Editar «${this.rol.etiqueta}»`;
    },

    grupos() {
      const mapa = {};
      this.permisos.forEach((p) => {
        (mapa[p.grupo] = mapa[p.grupo] || []).push(p);
      });
      return Object.keys(mapa).map((nombre) => ({ nombre, permisos: mapa[nombre] }));
    },
  },

  methods: {
    // Nadie puede conceder un permiso que no tiene (el backend también lo impide).
    puedeConceder(permiso) {
      return this.$store.getters.puede(permiso);
    },

    alAbrir() {
      this.etiqueta = this.rol ? this.rol.etiqueta : "";
      this.descripcion = this.rol ? this.rol.descripcion || "" : "";
      this.seleccion = this.rol ? [...this.rol.permisos] : [];
      this.guardando = false;
    },

    async guardar() {
      if (this.etiqueta.length < 3) {
        avisar(this, "El nombre del rol debe tener al menos 3 caracteres.", "warning");
        return;
      }

      const payload = {
        etiqueta: this.etiqueta,
        descripcion: this.descripcion || null,
        permisos: this.seleccion,
      };

      this.guardando = true;
      try {
        const response = this.rol
          ? await axios.put(`/roles/${this.rol.id}`, payload)
          : await axios.post("/roles", payload);

        avisar(
          this,
          this.rol
            ? `Se han guardado los permisos de «${this.etiqueta}». Los usuarios con este rol deberán volver a iniciar sesión si les afecta.`
            : `El rol «${this.etiqueta}» ya se puede asignar a los usuarios.`,
          "success",
          this.rol ? "Rol modificado" : "Rol creado"
        );
        this.$emit("guardado", response.data.roles);
        this.cerrar();
      } catch (error) {
        avisarError(this, error, "No se pudo guardar el rol.");
      } finally {
        this.guardando = false;
      }
    },

    cerrar() {
      this.$bvModal.hide(this.modalId);
    },
  },
};
</script>

<style scoped>
.mr-grupo {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  border: 0;
}

.mr-grupo-title {
  grid-column: 1 / -1;
  margin: 4px 0 0;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.mr-permiso {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 12px;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  background: var(--sd-surface);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.mr-permiso.is-on {
  border-color: var(--sd-primary);
  background: var(--sd-primary-soft);
}

.mr-permiso input {
  margin-top: 3px;
  accent-color: var(--sd-primary);
}

.mr-permiso > span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mr-permiso-nombre {
  font-weight: 600;
  color: var(--sd-text);
}

.mr-grupo:disabled .mr-permiso {
  cursor: default;
}
</style>
